import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "../style.scss";
import { useSelector, useDispatch } from "react-redux";
import {
    selectUserId,
    selectTeamMembers,
    selectTeamMembersLoading,
} from "data/user/selectors";
import LoadablePanel from "components/hoc/loadable/Panel";
import { getTeamMembers } from "data/user/actions";
import config from "config";

const TeamsBatchDailer = ({ isOpen, onClose, modalType, clicktoDial = false }) => {
    const {
        constants: {
            API: { PRICING_UPDATE_URL },
        },
    } = config;

    const dispatch = useDispatch();
    const userID = useSelector(selectUserId);
    const isTeamMemberLoading = useSelector(selectTeamMembersLoading);
    const teamMembers = useSelector(selectTeamMembers) || [];

    const teamFullMembers = teamMembers.filter(
        (m) => m.userType === "TEAM_FULL"
    );

    const members = teamFullMembers.map((m) => ({
        id: m.userId,
        name: `${m.firstName || ""} ${m.lastName || ""}`,
        // enabled: clicktoDial
        //     ? !!m.allowClickToDial
        //     : !!m.pushToBatchDialer,
    }));

    const idList = members.map((m) => m.id);

    const [toggles, setToggles] = useState({});
    const [enableAll, setEnableAll] = useState(false);

    /** Fetch team members on open */
    useEffect(() => {
        if (isOpen && modalType === "team") {
            dispatch(getTeamMembers(userID));
        }
    }, [isOpen, modalType, userID, dispatch]);

    useEffect(() => {
        if (!isOpen) return;

        const initial = {};
        teamFullMembers.forEach((m) => {
            initial[m.userId] = clicktoDial
                ? !!m.allowClickToDial
                : !!m.pushToBatchDialer;
        });

        setToggles(initial);

        const allOn =
            teamFullMembers.length > 0 &&
            teamFullMembers.every((m) =>
                clicktoDial
                    ? !!m.allowClickToDial
                    : !!m.pushToBatchDialer
            );

        setEnableAll(allOn);
    }, [isOpen, teamMembers, clicktoDial]);

    useEffect(() => {
        if (!idList.length) {
            setEnableAll(false);
            return;
        }

        const allOn = idList.every((id) => !!toggles[id]);
        setEnableAll(allOn);
    }, [toggles, idList]);

    const handleMainToggle = () => {
        const newVal = !enableAll;
        setEnableAll(newVal);

        const updated = {};
        idList.forEach((id) => {
            updated[id] = newVal;
        });

        setToggles(updated);
    };

    const handleMemberToggle = (id) => {
        setToggles((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const buildPayload = () =>
        teamFullMembers.map((member) =>
            clicktoDial
                ? {
                      teamMemberId: member.userId,
                      clickToDial: !!toggles[member.userId],
                  }
                : {
                      teamMemberId: member.userId,
                      pushToBatchDialer: !!toggles[member.userId],
                  }
        );

    const handleConfirm = () => {
        const payload = buildPayload();

        const endpoint = clicktoDial
            ? "/ps/team/members/clicktodial-access"
            : "/ps/team/member/batchdialer-access";

        fetch(`${PRICING_UPDATE_URL}${endpoint}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "x-auth-token": window.appAuthToken,
            },
            body: JSON.stringify(payload),
        })
            .then(async (res) => {
                if (res.status === 200) {
                    // Refresh team members so other widgets (e.g. Edit Profile modal)
                    dispatch(getTeamMembers(userID));
                    onClose();
                }
            })
            .catch(() => {
                // Optional: show error toast
            });
    };

    const title = clicktoDial
        ? "Click to Dial Team Permissions"
        : "Push to BatchDialer Team Permission";

    const instruction = clicktoDial
        ? "Available to Full Access team members only. Once enabled, the team member will use the selected phone numbers to make calls within PropStream."
        : "Available to Full Access team members only. Once enabled, they\ncan add leads to BatchDialer campaigns.";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCloseButton={false}
            uniqId="TeamPermissionModal"
            width="auto"
            borderRadius="12px"
            modalClassName={css.batchDialerModal}
            className={classNames(css.clsAccPageModals, css.clsBillingModal)}
        >
            <LoadablePanel loading={isTeamMemberLoading}>
                <div className={css.modalContainer}>
                    <div className={css.headertxt}>
                        <span>{title}</span>
                    </div>

                    <div className={css.instructionText}>
                        <span>
                            {instruction.split("\n").map((line, idx) => (
                                <React.Fragment key={idx}>
                                    {line}
                                    <br />
                                </React.Fragment>
                            ))}
                        </span>
                    </div>

                    <div className={css.teamBlock}>
                        <div className={css.teamRow}>
                            <div className={css.rowLeft}>
                                <span className={css.rowLabel}>
                                    Enable for All Full Access Team Members
                                </span>
                            </div>

                            <div
                                className={`${css.toggleSwitch} ${
                                    enableAll ? css.toggleOn : css.toggleOff
                                }`}
                                onClick={handleMainToggle}
                            >
                                <div className={css.toggleCircle}></div>
                            </div>
                        </div>

                        <div className={css.scrollArea}>
                            <div className={css.teamBlock}>
                                {members.map((member) => (
                                    <div
                                        key={member.id}
                                        className={css.teamRowChild}
                                    >
                                        <div className={css.rowLeft}>
                                            <div className={css.avatar}></div>
                                            <span className={css.rowLabel}>
                                                {member.name}
                                            </span>
                                        </div>

                                        <div
                                            className={`${css.toggleSwitch} ${
                                                toggles[member.id]
                                                    ? css.toggleOn
                                                    : css.toggleOff
                                            }`}
                                            onClick={() =>
                                                handleMemberToggle(member.id)
                                            }
                                        >
                                            <div
                                                className={css.toggleCircle}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={css.buttonWrapper}>
                        <button
                            className={css.cancelBtn}
                            onClick={onClose}
                        >
                            <span className={css.cancelBtnLabel}>
                                Cancel
                            </span>
                        </button>

                        <button
                            className={css.connectModalBtn}
                            onClick={handleConfirm}
                        >
                            <span className={css.connectBtnLabel}>
                                Confirm
                            </span>
                        </button>
                    </div>
                </div>
            </LoadablePanel>
        </Modal>
    );
};

export default TeamsBatchDailer;
